import { useEffect, useState } from "react";
import styles from "./mapping.module.css";
import { Row, type RowChange } from "./Row";
import {
  scoreOf,
  type Expand,
  type GroupDto,
  type RowMeta,
  type TransformDto,
} from "./types";

export function Group({
  group,
  entityId,
  onChange,
  expand = null,
  depth = 0,
  meta = {},
  transforms,
  previews = {},
  failures = {},
  readonly = false,
}: {
  group: GroupDto;
  entityId: string | undefined;
  onChange: RowChange;
  expand?: Expand | null | undefined;
  depth?: number | undefined;
  meta?: Record<string, RowMeta> | undefined;
  transforms?: TransformDto[] | undefined;
  previews?: Record<string, string> | undefined;
  failures?: Record<string, string> | undefined;
  readonly?: boolean | undefined;
}) {
  // Children of a collapsed group are unmounted, so seed from the last broadcast:
  // a subtree remounted after "Collapse all" must not come back expanded.
  const [open, setOpen] = useState(expand ? expand.open : true);

  // `expand` is a fresh {open, seq} object per Expand/Collapse-all click; the seq
  // makes two clicks of the SAME button re-fire, so a group toggled open by hand
  // still snaps shut on the next "Collapse all". null = no broadcast yet.
  useEffect(() => {
    if (expand) setOpen(expand.open);
  }, [expand]);

  const { covered, applicable } = scoreOf(group);
  const full = applicable > 0 && covered === applicable;

  return (
    <section className={styles.group} style={{ marginLeft: depth * 16 }}>
      <h2 className={styles.groupHead} onClick={() => setOpen((o) => !o)}>
        <span className={styles.groupToggle}>{open ? "▾" : "▸"}</span>
        <span className={styles.groupTitle}>{group.title}</span>
        <span className={`${styles.groupBadge}${full ? ` ${styles.groupBadgeFull}` : ""}`}>
          {covered}/{applicable}
        </span>
      </h2>
      {open && (
        <>
          {group.rows.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>OTA field</th>
                    <th>Mode</th>
                    <th>Source / value</th>
                    <th>Sample</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <Row
                      key={row.ota}
                      row={row}
                      entityId={entityId}
                      meta={meta[row.ota]}
                      transforms={transforms}
                      preview={previews[row.ota]}
                      failure={failures[row.ota]}
                      readonly={readonly}
                      onChange={onChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {group.groups.map((child) => (
            <Group
              key={child.key}
              group={child}
              entityId={entityId}
              onChange={onChange}
              expand={expand}
              depth={depth + 1}
              meta={meta}
              transforms={transforms}
              previews={previews}
              failures={failures}
              readonly={readonly}
            />
          ))}
        </>
      )}
    </section>
  );
}
